import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Initialize gtag globally to prevent "gtag is not defined" errors
if (typeof window !== 'undefined') {
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function() { window.dataLayer.push(arguments); };
}

// Map routes to page IDs for filtering
const ROUTE_TO_PAGE_ID = {
  '/': 'home',
  '/apply-online': 'apply-online',
  '/blogs': 'blogs',
  '/about-one92': 'about',
  '/privacy-policy': 'privacy-policy',
};

// Dynamic route patterns
const DYNAMIC_ROUTES = [
  { pattern: /^\/apply\/[^/]+\/form$/, pageId: 'visa-form' },
  { pattern: /^\/apply\/[^/]+$/, pageId: 'visa-details' },
  { pattern: /^\/blogs\/[^/]+$/, pageId: 'blog-detail' },
  { pattern: /^\/service\/[^/]+$/, pageId: 'service-detail' },
  { pattern: /^\/public\/proposal\/[^/]+$/, pageId: 'public-proposal' },
  { pattern: /^\/public\/agreement\/[^/]+$/, pageId: 'public-agreement' },
  { pattern: /^\/public\/welcome\/eta\/[^/]+$/, pageId: 'public-eta' },
  { pattern: /^\/promo\/[^/]+$/, pageId: 'promo-landing' },
];

// Routes to EXCLUDE from script injection
const EXCLUDED_ROUTES = [
  '/admin',
  '/login',
  '/client-login',
  '/client-portal',
];

/**
 * TrackingScriptInjector Component
 * 
 * Injects third-party tracking scripts (Google Analytics, Meta Pixel, etc.)
 * into the DOM based on configuration from the backend.
 * 
 * Features:
 * - SPA re-render protection (scripts load only once per page)
 * - Async loading with silent failure
 * - Environment-aware injection
 * - Page-specific script filtering
 */
export default function TrackingScriptInjector() {
  const location = useLocation();
  const loadedScriptsRef = useRef(new Set());
  const lastPathRef = useRef('');

  // Get page ID from current path
  const getPageId = useCallback((pathname) => {
    // Check static routes first
    if (ROUTE_TO_PAGE_ID[pathname]) {
      return ROUTE_TO_PAGE_ID[pathname];
    }

    // Check dynamic routes
    for (const route of DYNAMIC_ROUTES) {
      if (route.pattern.test(pathname)) {
        return route.pageId;
      }
    }

    return null;
  }, []);

  // Check if current route should have scripts injected
  const shouldInjectScripts = useCallback((pathname) => {
    // Check if route starts with any excluded path
    for (const excluded of EXCLUDED_ROUTES) {
      if (pathname.startsWith(excluded)) {
        return false;
      }
    }
    return true;
  }, []);

  // Check if script should load for current page
  const shouldLoadScript = useCallback((script, currentPageId) => {
    // If no pages selected, load on all public pages
    if (!script.selected_pages || script.selected_pages.length === 0) {
      return true;
    }
    // Check if current page is in selected pages
    return script.selected_pages.includes(currentPageId);
  }, []);

  // Safely execute script code
  const executeScript = useCallback((scriptCode, dedupKey) => {
    try {
      // Check if already loaded (SPA protection)
      if (loadedScriptsRef.current.has(dedupKey)) {
        return;
      }

      // Mark as loaded
      loadedScriptsRef.current.add(dedupKey);

      // Create a script element
      const scriptElement = document.createElement('script');
      scriptElement.setAttribute('data-dedup-key', dedupKey);
      scriptElement.setAttribute('data-tracking-script', 'true');
      
      // Check if the script code is an inline script or external
      if (scriptCode.includes('<script')) {
        // Extract script content from HTML tags
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = scriptCode;
        
        const scripts = tempDiv.querySelectorAll('script');
        scripts.forEach((script) => {
          const newScript = document.createElement('script');
          
          // Copy attributes
          Array.from(script.attributes).forEach(attr => {
            newScript.setAttribute(attr.name, attr.value);
          });
          
          newScript.setAttribute('data-dedup-key', dedupKey);
          newScript.setAttribute('data-tracking-script', 'true');
          newScript.async = true;
          
          // Copy content if inline
          if (script.textContent) {
            // Wrap gtag calls to prevent errors if gtag is not yet defined
            let scriptContent = script.textContent;
            if (scriptContent.includes('gtag(') && !scriptContent.includes('window.dataLayer')) {
              // Ensure dataLayer and gtag are defined before the script runs
              scriptContent = `
                window.dataLayer = window.dataLayer || [];
                window.gtag = window.gtag || function(){dataLayer.push(arguments);};
                ${scriptContent}
              `;
            }
            newScript.textContent = scriptContent;
          }
          
          document.head.appendChild(newScript);
        });

        // Also handle noscript and other elements
        const noScripts = tempDiv.querySelectorAll('noscript');
        noScripts.forEach((noscript) => {
          const newNoscript = document.createElement('noscript');
          newNoscript.innerHTML = noscript.innerHTML;
          newNoscript.setAttribute('data-dedup-key', dedupKey);
          document.body.appendChild(newNoscript);
        });
      } else {
        // Pure JavaScript code
        scriptElement.textContent = scriptCode;
        scriptElement.async = true;
        document.head.appendChild(scriptElement);
      }
    } catch (error) {
      // Silent failure - don't break the app
      console.warn(`[TrackingScripts] Failed to load script (${dedupKey}):`, error.message);
    }
  }, []);

  // Inject scripts based on placement and return loaded dedup_keys
  const injectScripts = useCallback((scripts, placement, currentPageId) => {
    if (!scripts || scripts.length === 0) return [];

    const loadedKeys = [];
    scripts.forEach((script) => {
      // Check if script should load for this page
      if (!shouldLoadScript(script, currentPageId)) {
        return;
      }

      // Check if already loaded (SPA protection)
      if (loadedScriptsRef.current.has(script.dedup_key)) {
        return;
      }

      // Execute the script
      executeScript(script.script_code, script.dedup_key);
      loadedKeys.push(script.dedup_key);
    });
    return loadedKeys;
  }, [shouldLoadScript, executeScript]);

  // Track script loads on the backend
  const trackScriptLoads = useCallback(async (dedupKeys) => {
    if (!dedupKeys || dedupKeys.length === 0) return;
    
    try {
      await fetch(`${BACKEND_URL}/api/tracking-scripts/track-load`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dedupKeys)
      });
    } catch (error) {
      // Silent failure - tracking shouldn't break the app
      console.warn('[TrackingScripts] Failed to track script loads:', error.message);
    }
  }, []);

  // Fetch and inject scripts
  const loadTrackingScripts = useCallback(async () => {
    const pathname = location.pathname;

    // Skip if on excluded route
    if (!shouldInjectScripts(pathname)) {
      return;
    }

    // Get current page ID
    const currentPageId = getPageId(pathname);

    try {
      const response = await fetch(`${BACKEND_URL}/api/tracking-scripts/active`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Inject scripts by placement and collect loaded dedup_keys
      const loadedKeys = [
        ...injectScripts(data.head, 'head', currentPageId),
        ...injectScripts(data['body-top'], 'body-top', currentPageId),
        ...injectScripts(data['body-bottom'], 'body-bottom', currentPageId)
      ];

      // Track loads on the backend (fire and forget)
      if (loadedKeys.length > 0) {
        trackScriptLoads(loadedKeys);
      }

    } catch (error) {
      // Silent failure
      console.warn('[TrackingScripts] Failed to fetch tracking scripts:', error.message);
    }
  }, [location.pathname, shouldInjectScripts, getPageId, injectScripts, trackScriptLoads]);

  // Load scripts on mount and route changes
  useEffect(() => {
    // Only reload if path actually changed (SPA protection)
    if (lastPathRef.current !== location.pathname) {
      lastPathRef.current = location.pathname;
      loadTrackingScripts();
    }
  }, [location.pathname, loadTrackingScripts]);

  // This component doesn't render anything
  return null;
}
